<template>
  <iRow
    align="between"
    class="navbar"
    vertical-align="middle"
    wrap="nowrap"
  >
    <iRow
      width="hug"
      height="fill"
      class="nav-item-padding"
      vertical-align="middle"
      align="between"
      wrap="nowrap"
    >
      <Logo ref="logo" :show-title="$vssDesktop" />
      <iIcon
        v-if="canShowSmallMenu"
        name="material-symbols:menu-rounded"
        @click="drawerVisible = !drawerVisible"
      />
    </iRow>
    <iColumn v-if="canShowBigMenu" class="nav-items" vertical-align="middle">
      <iMenu
        v-if="canShowNavigationMenu"
        orientation="horizontal"
        :items="parsedPrimaryNavDataWithNoIcons"
        :active-item="activeItem"
      />
    </iColumn>

    <iRow width="hug" wrap="nowrap" vertical-align="middle">
      <iDropdown
        v-if="canShowSmallSearch"
        :teleport-to-body="false"
        :show-dropdown="searchDropdownVisible"
        @close="searchDropdownVisible = false"
      >
        <template #trigger>
          <iIcon name="material-symbols:search-rounded" @click="searchDropdownVisible = true" />
        </template>
        <template #overlay>
          <iTextInput
            v-model="globalSearchText"
            right-icon="material-symbols:search-rounded"
            :width="200"
            size="small"
            placeholder="Search"
          />
        </template>
      </iDropdown>
      <iTextInput
        v-else-if="canShowNavigationMenu"
        v-model="globalSearchText"
        right-icon="material-symbols:search-rounded"
        :width="200"
        size="small"
        placeholder="Search"
      />

      <iLink v-if="isCreator" :href="`/digital-wallet/org/${orgId}`">
        {{ digitalWalletBalanceString }}
      </iLink>
      <iDropdown
        v-if="areNotificationsEnabled"
        :teleport-to-body="false"
        :show-dropdown="showNotificationDropdown"
        @close="closeNotificationDropdown"
      >
        <template #trigger>
          <iIcon name="material-symbols:notifications-outline-rounded" @click="openNotificationDropdown" />
        </template>
        <template #overlay>
          <iRow
            :max-height="600"
            :width="400"
            wrap="nowrap"
            style="margin-top: 15px; margin-bottom: 15px;"
            overflow="scroll"
          >
            <SmartFeedV2
              style="width: 400px"
              :source="useNotificationFeedStore"
              :show-filters="false"
              default-feed-layout="list"
              default-entity-size="small"
            >
              <template #empty-state>
                <iRow width="fill" align="center">
                  <iText variant="subtle">
                    You have no notifications
                  </iText>
                </iRow>
              </template>
            </SmartFeedV2>
          </iRow>
        </template>
      </iDropdown>

      <iDropdownMenu
        :items="parsedSecondaryNavData"
        :show-dropdown="showAvatarDropdown"
        @close="closeAvatarDropdown"
      >
        <template #trigger>
          <iColumn gap="extraSmall" vertical-align="middle">
            <iRow
              class="my-account-btn"
              gap="small"
              height="fill"
              vertical-align="middle"
              width="hug"
              wrap="nowrap"
              @click="toggleAvatarDropdown"
            >
              <iColumn vertical-align="middle">
                <AvatarImage :src="userData.avatar_url || ''" size="32px" />
              </iColumn>
              <iColumn v-if="$vssDesktop" gap="extraSmall">
                <iSpace />
                <iText :max-width="125" :wrap="false" ellipsis>
                  {{ userData.first_name }}
                </iText>
              </iColumn>
            </iRow>
            <iSpace />
          </iColumn>
        </template>
      </iDropdownMenu>
    </iRow>
  </iRow>
  <teleport to="body">
    <iDrawer v-model:is-open="drawerVisible">
      <template #header>
        <iRow align="between" vertical-align="middle">
          <Logo ref="drawerLogo" />
          <iIcon name="material-symbols:close-rounded" @click="drawerVisible = false" />
        </iRow>
      </template>

      <iColumn class="drawer-navigation">
        <iMenu
          :items="parsedPrimaryNavData"
          :active-item="activeItem"
          orientation="vertical"
        />
      </iColumn>
    </iDrawer>
  </teleport>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { useFanPublicationStore } from "%/stores/fan-publication-store";
import { useUserStore } from "~/stores/user-store";
import { useConfigurationStore } from "%/stores/configuration-store";
import AvatarImage from "%/components/AvatarImage";
import { useGeneralStore } from "%/stores/general-store";
import { useStyleStore, useWindowStore } from "@vcl";
import Logo from "@/components/Logo";
import { usePaymentStore } from "%/stores/payments-store";
import { filterMenuItems } from "@/helpers/capabilities-helper";
import { MAIN_NAV_PRIMARY, MAIN_NAV_SECONDARY } from "@/constants/nav-constants";
import { parseUrlVars } from "@/helpers/parseUrlVars";
import { approvedWaitlistStatuses, waitlistStatus } from "@/enums/waitlist-enum";
import { useNotificationStore } from "%/stores/notification-store";
import { useFeatureStore } from "%/stores/feature-store";
import SmartFeedV2 from "SmartFeed/components/SmartFeedV2.vue";
import SmartFeedEngine from "@/smart-feed/smart-feed-engine";

export default {
  name: "MainNav",
  components: {
    SmartFeedV2,
    Logo,
    AvatarImage,
  },
  props: {
    activeItem: {
      type: String,
      required: false,
      default: "",
    },
  },
  data() {
    return {
      showAvatarDropdown: false,
      showNotificationDropdown: false,
      waitlistStatus,
      collapsedItems: [],
      globalSearchText: "",
      drawerVisible: false,
      searchDropdownVisible: false,
    };
  },
  computed: {
    ...mapState(useConfigurationStore, [
      "primaryNavigation",
      "isSignupAndLoginEnabled",
      "areSubscriptionsAvailable",
    ]),
    ...mapState(useFanPublicationStore, [
      "publicationName",
      "canConfigurePublication",
    ]),
    ...mapState(useGeneralStore, [
      "userData",
      "userId",
      "orgData",
      "orgId",
      "isStaging",
      "isCreator",
    ]),
    ...mapState(useWindowStore, [
      "$vssMobile",
      "$vssLargeMobile",
      "$vssTablet",
      "$vssDesktop",
    ]),
    ...mapState(usePaymentStore, [
      "digitalWalletBalanceString",
    ]),
    ...mapState(useStyleStore, [
      "appThemeModes",
    ]),
    areNotificationsEnabled() {
      return useFeatureStore().isFeatureEnabled("notifications");
    },
    canShowNavigationMenu() {
      return Object.values(approvedWaitlistStatuses).includes(this.orgData.waitlist_status);
    },
    canShowBigMenu() {
      const notMobileAndNotCreator = !this.$vssMobile && !this.isCreator;
      const isCreatorAndOnDesktop = this.isCreator && this.$vssDesktop;
      return notMobileAndNotCreator || isCreatorAndOnDesktop;
    },
    canShowSmallMenu() {
      const canShowNavMenuNotCreatorOnMobile = this.$vssMobile && !this.isCreator && this.canShowNavigationMenu;
      const canShowNavMenuIsCreatorNotDesktop = this.isCreator && !this.$vssDesktop && this.canShowNavigationMenu;
      return canShowNavMenuNotCreatorOnMobile || canShowNavMenuIsCreatorNotDesktop;
    },
    canShowSmallSearch() {
      const canShowNavMenuNotDesktopNotTablet = !this.$vssDesktop && !this.$vssTablet && this.canShowNavigationMenu;
      const canShowNavMenuIsCreatorNotDesktop = this.isCreator && !this.$vssDesktop && this.canShowNavigationMenu;
      return canShowNavMenuNotDesktopNotTablet || canShowNavMenuIsCreatorNotDesktop;
    },
    parsedPrimaryNavData() {
      const filteredNavItems = filterMenuItems(MAIN_NAV_PRIMARY);
      return parseUrlVars(filteredNavItems, { "{org_id}": this.orgId, "{user_id}": this.userId });
    },
    parsedPrimaryNavDataWithNoIcons() {
      const filteredNavItems = filterMenuItems(MAIN_NAV_PRIMARY, true);
      return parseUrlVars(filteredNavItems, { "{org_id}": this.orgId, "{user_id}": this.userId });
    },
    parsedSecondaryNavData() {
      const filteredNavItems = filterMenuItems(MAIN_NAV_SECONDARY);
      return parseUrlVars(filteredNavItems, { "{org_id}": this.orgId, "{user_id}": this.userId });
    },
    isUserLoggedIn() {
      return this.userId > 0;
    },
    useNotificationFeedStore() {
      return SmartFeedEngine.getFeedPiniaStore("NotificationFeedSource");
    },
  },
  watch: {
    appThemeModes: {
      immediate: true,
      deep: true,
      handler() {
        this.$exposeStyleGuide("main-nav");
      },
    },
  },
  mounted() {
    useWindowStore().init();
    this.$exposeStyleGuide("main-nav");
  },
  beforeUnmount() {
    useWindowStore.remove();
  },
  methods: {
    toggleAvatarDropdown() {
      this.showAvatarDropdown = !this.showAvatarDropdown;
    },
    closeAvatarDropdown() {
      this.showAvatarDropdown = false;
    },
    closeNotificationDropdown() {
      this.showNotificationDropdown = false;
    },
    openNotificationDropdown() {
      this.showNotificationDropdown = !this.showNotificationDropdown;
      this.requestPermission();
    },
    ...mapActions(useGeneralStore, [
      "getRelativeHref",
      "redirectTo",
    ]),
    ...mapActions(useNotificationStore, ["requestPermission"]),
    ...mapActions(useUserStore, [
      "logoutUser",
    ]),
  },
  styleGuide: () => ({
    navPaddingBlock: { "size.spacing": "extraSmall" },
    navPaddingInline: { "size.spacing": "large" },
    backgroundColor: { "color.background": "paper" },
    selectedItemBackgroundColor: { "color.background": "notice" },
    menuItemHoverBackgroundColor: { "color.background": "light" },
  }),
};
</script>

<style lang="scss" scoped>
.nav-items {
  overflow: hidden;
}

:deep(.drawer-navigation) {
  .menu-item.active:not(.focused-item) {
    background-color: var(--main-nav-selectedItemBackgroundColor) !important;
  }

  .menu-item:hover {
    background-color: var(--main-nav-menuItemHoverBackgroundColor) !important;
  }
}

.navbar {
  padding: v-bind('$getStyles.navPaddingBlock') v-bind('$getStyles.navPaddingInline') 0 v-bind('$getStyles.navPaddingInline');
  //box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  background-color: v-bind('$getStyles.backgroundColor');
}

:deep(.nav-link) {
  white-space: nowrap;
}

:deep(.drawer-navigation .menu.vertical) {
  width: 100%;
}
</style>
<!--eslint-disable-next-line vue-scoped-css/require-scoped vue-scoped-css/enforce-style-type -->
<style scoped>
:deep(.infinite-scroll-container) {
  width: 400px !important;
}
</style>
